import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ORCHESTRATION_ENGINE_URL as API_BASE } from '../lib/engineUrls';
import { 
  Settings, 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Play,
  Pause,
  Eye,
  Search,
  Filter,
  ChevronRight,
  ChevronDown,
  Database,
  Workflow,
  Clock,
  Activity,
  GitBranch,
  FileText,
  BarChart3,
  XCircle,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';


interface Job {
  jobId: string;
  name: string;
  description: string;
  jobType: string;
  environment: string;
  isEnabled: boolean;
  specJson: any;
}

interface JobRun {
  runId: string;
  jobId: string;
  status: string;
  startTime: string;
  endTime: string;
  mode: string;
  jobVersion: string;
  summaryJson?: any;
  errorJson?: any;
}

const OrchestrationEngine: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [runs, setRuns] = useState<JobRun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activePage, setActivePage] = useState<'overview' | 'jobs' | 'runs' | 'items' | 'diff'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [healthStatus, setHealthStatus] = useState<any>(null);

  useEffect(() => {
    loadHealth();
    loadJobs();
  }, []);

  useEffect(() => {
    if (selectedJob) {
      loadRuns(selectedJob.jobId);
    }
  }, [selectedJob]);

  const loadHealth = async () => {
    try {
      const response = await fetch(`${API_BASE}/health`);
      if (response.ok) {
        const data = await response.json();
        setHealthStatus(data);
      }
    } catch (error) {
      console.error('Failed to load health:', error);
    }
  };

  const loadJobs = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/jobs`, {
        headers: {
          'Authorization': 'Bearer bypass-auth-for-now'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRuns = async (jobId: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/jobs/${jobId}/runs`, {
        headers: {
          'Authorization': 'Bearer bypass-auth-for-now'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRuns(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load runs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRunJob = async (jobId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/jobs/${jobId}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer bypass-auth-for-now'
        },
        body: JSON.stringify({ mode: 'normal' })
      });
      if (response.ok) {
        await loadRuns(jobId);
        await loadJobs();
      }
    } catch (error) {
      console.error('Failed to run job:', error);
    }
  };

  const handleToggleJob = async (jobId: string, enabled: boolean) => {
    try {
      const endpoint = enabled ? 'enable' : 'disable';
      const response = await fetch(`${API_BASE}/api/jobs/${jobId}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer bypass-auth-for-now'
        }
      });
      if (response.ok) {
        await loadJobs();
      }
    } catch (error) {
      console.error('Failed to toggle job:', error);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = searchTerm === '' || 
      job.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || job.jobType === filterType;
    return matchesSearch && matchesType;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]';
      case 'failure': return 'text-[var(--coral)] bg-[var(--coral-dim)]';
      case 'running': return 'text-[var(--text-sec)] bg-[var(--slate-dim)]';
      case 'partial': return 'text-[var(--mustard)] bg-[var(--mustard-dim)]';
      default: return 'text-[var(--text-sec)] bg-[var(--surface)]';
    }
  };

  const getJobTypeColor = (type: string) => {
    switch (type) {
      case 'CONSUMPTIVE': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'ENUMERATIVE': return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
      case 'MATERIALIZATION': return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
      case 'COORDINATIVE': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
      default: return 'bg-[var(--hover-tint)] text-[var(--text-pri)]';
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      {/* Header */}
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/elevation/engines" className="text-[var(--mustard)] hover:text-[var(--mustard)]">
                ← Back to Engines
              </Link>
              <div className="h-12 w-12 bg-consulting-slate-blue-100 flex items-center justify-center">
                <Workflow className="h-6 w-6 text-consulting-slate-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-[var(--text-pri)]">Orchestration Engine</h1>
                <p className="text-[var(--text-sec)]">Job Execution State Model (JESM)</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {healthStatus && (
                <div className={`px-4 py-2  ${healthStatus.status === 'healthy' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--coral-dim)] text-[var(--coral)]'}`}>
                  {healthStatus.status === 'healthy' ? <CheckCircle className="h-5 w-5 inline mr-2" /> : <XCircle className="h-5 w-5 inline mr-2" />}
                  {healthStatus.status}
                </div>
              )}
              <button
                onClick={loadJobs}
                className="px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'jobs', label: 'Jobs', icon: Database },
              { id: 'runs', label: 'Runs', icon: Clock },
              { id: 'items', label: 'Items', icon: FileText },
              { id: 'diff', label: 'Diff', icon: GitBranch }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActivePage(id as any)}
                className={`flex items-center gap-2 px-4 py-4 border-b-2 ${
                  activePage === id
                    ? 'border-consulting-slate-blue-600 text-consulting-slate-blue-600'
                    : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:border-[var(--rule)]'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activePage === 'overview' && (
          <div className="space-y-6">
            <div className="bg-[var(--bg-raised)] shadow p-6">
              <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-4">About JESM</h2>
              <p className="text-[var(--text-sec)] mb-4">
                The Job Execution State Model (JESM) is a system that defines, enforces, and exposes the stateful execution semantics 
                of scheduled and operator-triggered jobs. JESM provides full lifecycle management with explicit state tracking, 
                reprocessing policies, and complete audit trails.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="bg-[var(--slate-dim)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">CONSUMPTIVE</h3>
                  <p className="text-sm text-[var(--text-sec)]">Supplied discrete inputs; per-input state; deduplication required</p>
                </div>
                <div className="bg-[var(--green-dim)] p-4">
                  <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-2">ENUMERATIVE</h3>
                  <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">Discovers candidates; qualify/skip semantics</p>
                </div>
                <div className="bg-[var(--slate-dim)] p-4">
                  <h3 className="font-semibold text-[var(--text-sec)] mb-2">MATERIALIZATION</h3>
                  <p className="text-sm text-[var(--text-sec)]">Derives or rebuilds secondary artifacts from source truth</p>
                </div>
                <div className="bg-[var(--mustard-dim)] p-4">
                  <h3 className="font-semibold text-[var(--mustard)] mb-2">COORDINATIVE</h3>
                  <p className="text-sm text-[var(--mustard)]">Orchestrates steps, dependencies, or other jobs</p>
                </div>
              </div>
            </div>

            <div className="bg-[var(--bg-raised)] shadow p-6">
              <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-4">System Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[var(--surface)] p-4">
                  <div className="text-3xl font-bold text-[var(--text-pri)]">{jobs.length}</div>
                  <div className="text-sm text-[var(--text-sec)]">Total Jobs</div>
                </div>
                <div className="bg-[var(--surface)] p-4">
                  <div className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{jobs.filter(j => j.isEnabled).length}</div>
                  <div className="text-sm text-[var(--text-sec)]">Enabled Jobs</div>
                </div>
                <div className="bg-[var(--surface)] p-4">
                  <div className="text-3xl font-bold text-[var(--text-ter)]">{runs.length}</div>
                  <div className="text-sm text-[var(--text-sec)]">Total Runs</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activePage === 'jobs' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-[var(--bg-raised)] shadow p-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[var(--text-ter)]" />
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="CONSUMPTIVE">Consumptive</option>
                  <option value="ENUMERATIVE">Enumerative</option>
                  <option value="MATERIALIZATION">Materialization</option>
                  <option value="COORDINATIVE">Coordinative</option>
                </select>
              </div>
            </div>

            {/* Jobs List */}
            <div className="bg-[var(--bg-raised)] shadow">
              {isLoading ? (
                <div className="p-8 text-center text-[var(--text-ter)]">Loading jobs...</div>
              ) : filteredJobs.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-ter)]">No jobs found</div>
              ) : (
                <div className="divide-y divide-[var(--rule)]">
                  {filteredJobs.map((job) => (
                    <div key={job.jobId} className="p-6 hover:bg-[var(--surface)]">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-[var(--text-pri)]">{job.name}</h3>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getJobTypeColor(job.jobType)}`}>
                              {job.jobType}
                            </span>
                            {job.isEnabled ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]">
                                Enabled
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-pri)]">
                                Disabled
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[var(--text-sec)] mb-2">{job.description}</p>
                          <div className="flex items-center gap-4 text-xs text-[var(--text-ter)]">
                            <span>Environment: {job.environment}</span>
                            <span>ID: {job.jobId}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleJob(job.jobId, !job.isEnabled)}
                            className={`px-3 py-1 rounded text-sm ${
                              job.isEnabled
                                ? 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                                : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)]'
                            }`}
                          >
                            {job.isEnabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedJob(job);
                              setActivePage('runs');
                            }}
                            className="px-3 py-1 bg-consulting-slate-blue-600 text-white rounded text-sm hover:bg-consulting-slate-blue-700 flex items-center gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            View
                          </button>
                          <button
                            onClick={() => handleRunJob(job.jobId)}
                            className="px-3 py-1 bg-[var(--slate)] text-white rounded text-sm hover:bg-[var(--slate)] flex items-center gap-1"
                            disabled={!job.isEnabled}
                          >
                            <Play className="h-4 w-4" />
                            Run
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activePage === 'runs' && (
          <div className="space-y-6">
            {selectedJob && (
              <div className="bg-[var(--bg-raised)] shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-[var(--text-pri)]">{selectedJob.name}</h2>
                    <p className="text-sm text-[var(--text-sec)]">Run History</p>
                  </div>
                  <button
                    onClick={() => setSelectedJob(null)}
                    className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                  >
                    Clear Selection
                  </button>
                </div>
              </div>
            )}

            <div className="bg-[var(--bg-raised)] shadow">
              {isLoading ? (
                <div className="p-8 text-center text-[var(--text-ter)]">Loading runs...</div>
              ) : runs.length === 0 ? (
                <div className="p-8 text-center text-[var(--text-ter)]">
                  {selectedJob ? 'No runs found for this job' : 'Select a job to view runs'}
                </div>
              ) : (
                <div className="divide-y divide-[var(--rule)]">
                  {runs.map((run) => (
                    <div key={run.runId} className="p-6 hover:bg-[var(--surface)]">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(run.status)}`}>
                              {run.status}
                            </span>
                            <span className="text-sm text-[var(--text-ter)]">Mode: {run.mode}</span>
                            <span className="text-sm text-[var(--text-ter)]">Version: {run.jobVersion}</span>
                          </div>
                          <div className="text-xs text-[var(--text-ter)] space-y-1">
                            <div>Run ID: {run.runId}</div>
                            <div>Started: {run.startTime ? new Date(run.startTime).toLocaleString() : 'N/A'}</div>
                            <div>Ended: {run.endTime ? new Date(run.endTime).toLocaleString() : 'N/A'}</div>
                          </div>
                          {run.errorJson && (
                            <div className="mt-2 p-2 bg-[var(--coral-dim)] rounded text-sm text-[var(--coral)]">
                              Error: {run.errorJson.message || 'Unknown error'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activePage === 'items' && (
          <div className="bg-[var(--bg-raised)] shadow p-8 text-center text-[var(--text-ter)]">
            Items view - Coming soon
          </div>
        )}

        {activePage === 'diff' && (
          <div className="bg-[var(--bg-raised)] shadow p-8 text-center text-[var(--text-ter)]">
            Diff view - Coming soon
          </div>
        )}
      </div>
    </div>
  );
};

export default OrchestrationEngine;








